import {
  Button,
  Collapse,
  Drawer,
  Empty,
  Input,
  Spin,
  Tabs,
  Tag,
  Upload,
  message as antdMessage,
} from "antd";
import {
  CheckOutlined,
  FileImageOutlined,
  InboxOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import type { UploadFile, RcFile } from "antd/es/upload/interface";
import { ContactAvatar } from "./ContactAvatar";
import { importContacts, listContacts } from "../api/contacts";
import type { Contact } from "../api/contacts";
import { createGroup } from "../api/groups";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: (groupId: string) => void;
};

export function CreateGroupDrawer({ open, onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [importerKey, setImporterKey] = useState(0);

  const refreshContacts = async () => {
    setLoading(true);
    try {
      const { contacts } = await listContacts("all");
      setContacts(contacts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setName("");
    setQ("");
    setSelected(new Set());
    setImporterKey((k) => k + 1);
    void refreshContacts();
  }, [open]);

  const filtered = useMemo(() => {
    if (!q) return contacts;
    const lc = q.toLowerCase();
    return contacts.filter(
      (c) => c.email.toLowerCase().includes(lc) || c.name?.toLowerCase().includes(lc),
    );
  }, [contacts, q]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onImported = async (contactIds: string[]) => {
    await refreshContacts();
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of contactIds) next.add(id);
      return next;
    });
  };

  const onSubmit = async () => {
    if (!name.trim()) {
      antdMessage.warning("请填群组名");
      return;
    }
    setSubmitting(true);
    try {
      const { group } = await createGroup(name.trim(), [...selected]);
      antdMessage.success("已创建");
      onCreated?.(group.id);
      onClose();
    } catch {
      antdMessage.error("创建失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title="创建群组"
      open={open}
      onClose={onClose}
      width={460}
      placement="right"
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, color: "var(--ant-color-text-secondary)" }}>
            已选 {selected.size} 人
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose}>取消</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={submitting}
              onClick={onSubmit}
            >
              创建
            </Button>
          </div>
        </div>
      }
    >
      <Input
        placeholder="群组名,例如:A 项目供应商"
        value={name}
        onChange={(e) => setName(e.target.value)}
        size="large"
        style={{ marginBottom: 12 }}
        maxLength={120}
      />

      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        items={[
          {
            key: "import",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <ThunderboltOutlined /> 批量导入邮箱
              </span>
            ),
            children: <ImportPanel key={importerKey} onImported={onImported} />,
          },
        ]}
      />

      <Input
        placeholder="搜索联系人"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        prefix={<SearchOutlined style={{ color: "var(--ant-color-text-secondary)" }} />}
        allowClear
        style={{ marginBottom: 12 }}
      />
      {loading ? (
        <div style={{ display: "grid", placeItems: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description="无联系人" />
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
          {filtered.map((c) => {
            const checked = selected.has(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => toggle(c.id)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 12px",
                    border: "1px solid",
                    borderColor: checked ? "var(--ant-color-primary)" : "var(--ant-color-border)",
                    background: checked
                      ? "color-mix(in srgb, var(--ant-color-primary) 8%, transparent)"
                      : "var(--ant-color-bg-container)",
                    color: "inherit",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <ContactAvatar email={c.email} name={c.name} tier={c.tier} size={32} interactive={false} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name || c.email}</div>
                    {c.name ? (
                      <div
                        style={{
                          fontSize: 14,
                          color: "var(--ant-color-text-secondary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.email}
                      </div>
                    ) : null}
                  </div>
                  {checked ? <Tag color="blue">已选</Tag> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}

type RecognizedBatch = {
  emails: string[];
  added: number;
  existing: number;
};

function ImportPanel({ onImported }: { onImported: (contactIds: string[]) => Promise<void> }) {
  const [mode, setMode] = useState<"text" | "image">("text");
  const [text, setText] = useState("");
  const [file, setFile] = useState<UploadFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<RecognizedBatch | null>(null);

  const reset = () => {
    setText("");
    setFile(null);
    setLast(null);
  };

  const fileToBase64 = (raw: File): Promise<{ base64: string; mime: string }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("read_failed"));
      reader.onload = () => {
        const result = String(reader.result ?? "");
        const idx = result.indexOf(",");
        if (idx < 0) {
          reject(new Error("invalid_data_url"));
          return;
        }
        resolve({ base64: result.slice(idx + 1), mime: raw.type || "image/png" });
      };
      reader.readAsDataURL(raw);
    });

  const run = async () => {
    if (mode === "text" && !text.trim()) {
      antdMessage.warning("请先粘贴文本");
      return;
    }
    if (mode === "image" && !file) {
      antdMessage.warning("请先上传图片");
      return;
    }
    setBusy(true);
    setLast(null);
    try {
      let payload: { text?: string; imageBase64?: string; imageMime?: string };
      if (mode === "image" && file) {
        const raw = (file.originFileObj ?? file) as File;
        if (raw.size > MAX_IMAGE_BYTES) {
          antdMessage.warning("图片不能超过 10MB");
          setBusy(false);
          return;
        }
        const { base64, mime } = await fileToBase64(raw);
        payload = { imageBase64: base64, imageMime: mime };
      } else {
        payload = { text };
      }
      const result = await importContacts(payload);
      if (result.emails.length === 0) {
        antdMessage.info("没识别到邮箱");
        setLast({ emails: [], added: 0, existing: 0 });
        return;
      }
      setLast({ emails: result.emails, added: result.added, existing: result.existing });
      antdMessage.success(
        `已识别 ${result.emails.length} 个邮箱(新增 ${result.added},已有 ${result.existing})`,
      );
      await onImported(result.contactIds);
      if (mode === "image") setFile(null);
      else setText("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "import_failed";
      antdMessage.error(`识别失败: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const beforeUpload = (raw: RcFile) => {
    if (!raw.type.startsWith("image/")) {
      antdMessage.warning("只支持图片文件");
      return Upload.LIST_IGNORE;
    }
    if (raw.size > MAX_IMAGE_BYTES) {
      antdMessage.warning("图片不能超过 10MB");
      return Upload.LIST_IGNORE;
    }
    setFile({
      uid: String(Date.now()),
      name: raw.name,
      status: "done",
      originFileObj: raw,
    });
    return false;
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Tabs
        size="small"
        activeKey={mode}
        onChange={(k) => setMode(k as "text" | "image")}
        items={[
          {
            key: "text",
            label: "粘贴文本",
            children: (
              <Input.TextArea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="粘贴含邮箱的文本(支持多个,以空格/逗号/换行分隔)"
                autoSize={{ minRows: 4, maxRows: 10 }}
                maxLength={50_000}
              />
            ),
          },
          {
            key: "image",
            label: (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <FileImageOutlined /> 上传图片
              </span>
            ),
            children: (
              <Upload.Dragger
                multiple={false}
                accept="image/*"
                beforeUpload={beforeUpload}
                showUploadList={false}
                fileList={file ? [file] : []}
                onRemove={() => setFile(null)}
                style={{ padding: 12 }}
              >
                <p style={{ margin: 0, fontSize: 22 }}>
                  <InboxOutlined />
                </p>
                <p style={{ margin: "6px 0 2px", fontSize: 14 }}>
                  {file ? file.name : "点击或拖入图片"}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: "var(--ant-color-text-secondary)" }}>
                  截图、表格图、名片照片均可 · ≤ 10MB
                </p>
              </Upload.Dragger>
            ),
          },
        ]}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--ant-color-text-secondary)" }}>
          {mode === "image" ? "AI Vision 识别需 3–8 秒" : "正则解析,瞬时完成"}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          {(text || file || last) && (
            <Button size="small" onClick={reset} disabled={busy}>
              清空
            </Button>
          )}
          <Button size="small" type="primary" loading={busy} onClick={run}>
            识别
          </Button>
        </div>
      </div>

      {last && last.emails.length > 0 ? (
        <div
          style={{
            background: "color-mix(in srgb, var(--ant-color-success) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--ant-color-success) 30%, transparent)",
            borderRadius: 8,
            padding: 8,
          }}
        >
          <div style={{ fontSize: 12, color: "var(--ant-color-text-secondary)", marginBottom: 4 }}>
            已自动加入下方列表并勾选 · 新增 {last.added} · 已有 {last.existing}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {last.emails.map((e) => (
              <Tag key={e} color="green" style={{ marginInlineEnd: 0 }}>
                {e}
              </Tag>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
